import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'
import debug from 'debug'
import PermissionError from '@/errors/permission_error'

const NotFound = () => import(
  /* webpackChunkName: "not_found" */
  '@/components/errors/not_found')
const InternalError = () => import(
  /* webpackChunkName: "internal_error" */
  '@/components/errors/internal_error')

// Layouts
const MainLayout = () => import(
  /* webpackChunkName: "main" */
  '@/layouts/main')
const PagesLayout = () => import(
  /* webpackChunkName: "pages" */
  '@/layouts/pages')

// Sessions
const OAuthCallback = () => import(
  /* webpackChunkName: "oauth_callback" */
  '@/components/sessions/oauth_callback')
const Signin = () => import(
  /* webpackChunkName: "new" */
  '@/components/sessions/new')

// Projects
const ProjectsIndex = () => import(
  /* webpackChunkName: "index" */
  '@/components/projects/index/index')
const ProjectsNew = () => import(
  /* webpackChunkName: "new" */
  '@/components/projects/new')
const ProjectCopy = () => import(
  /* webpackChunkName: "copy" */
  '@/components/projects/copy')
const ProjectEdit = () => import(
  /* webpackChunkName: "edit" */
  '@/components/projects/edit/edit')
const ProjectEditMods = () => import(
  /* webpackChunkName: "mods" */
  '@/components/projects/edit/mods')
const ProjectEditModal = () => import(
  /* webpackChunkName: "modal" */
  '@/components/projects/edit/modal')
const ProjectEditHistory = () => import(
  /* webpackChunkName: "history" */
  '@/components/projects/edit/history')
const ProjectEditGeneral = () => import(
  /* webpackChunkName: "general" */
  '@/components/projects/edit/general')
const ProjectEditShared = () => import(
  /* webpackChunkName: "shared" */
  '@/components/projects/edit/shared')
const ProjectEditSettings = () => import(
  /* webpackChunkName: "settings" */
  '@/components/projects/edit/settings')
const ProjectEditInfo = () => import(
  /* webpackChunkName: "info" */
  '@/components/projects/edit/info')
const ProjectEditBlock = () => import(
  /* webpackChunkName: "edit" */
  '@/components/projects/edit/blocks/edit')
const ProjectPreview = () => import(
  /* webpackChunkName: "preview" */
  '@/components/projects/preview')
const ImageMapTool = () => import(
  /* webpackChunkName: "image_map_tool" */
  '@/components/projects/edit/image_map_tool')
const TextEditor = () => import(
  '@/components/projects/edit/text_editor')

// Pages
const PagesGuide = () => import(
  /* webpackChunkName: "guide" */
  '@/components/pages/guide')
const PagesChangelog = () => import(
  /* webpackChunkName: "changelog" */
  '@/components/pages/changelog')

const trace = debug('router')

// for store last sub view
var LastSubView

Vue.use(Router)

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'root',
      redirect: { name: 'signin' },
      component: MainLayout,
      meta: { loginRequired: true },
      children: [
        {
          path: 'projects',
          name: 'projects#index',
          component: ProjectsIndex,
          children: [
            {
              path: 'new',
              name: 'projects#new',
              component: ProjectsNew,
              meta: { canCreateProjectRequired: true }
            },
            {
              path: 'copy',
              name: 'projects#copy',
              component: ProjectCopy,
              meta: { canCreateProjectRequired: true }
            }
          ]
        },
        {
          path: 'projects/created_by_me',
          name: 'projects#created_by_me',
          component: ProjectsIndex
        },
        {
          path: 'projects/published',
          name: 'projects#published',
          component: ProjectsIndex
        },
        {
          path: 'projects/unpublished',
          name: 'projects#unpublished',
          component: ProjectsIndex
        }
      ]
    },
    {
      path: '/signin',
      name: 'signin',
      component: Signin,
      beforeEnter(to, from, next) {
        const isLoggedIn = store.getters['account/isLoggedIn']
        if (isLoggedIn) {
          next({ name: 'projects#index' })
        } else {
          next()
        }
      }
    },
    {
      path: '/signout',
      name: 'signout',
      beforeEnter(to, from, next) {
        store.dispatch('account/logout')
          .then(() => router.push({ name: 'root' }))
      }
    },
    {
      path: '/auth/:provider/callback',
      component: OAuthCallback
    },
    {
      path: '/projects/:project_id',
      name: 'projects#show',
      redirect: { name: 'projects#edit' },
      meta: { loginRequired: true }
    },
    {
      path: '/projects/:project_id/edit',
      component: ProjectEdit,
      meta: {
        loginRequired: true,
        canEditProjectRequired: true
      },
      children: [
        {
          path: '',
          name: 'projects#edit',
          component: ProjectEditMods
        },
        {
          path: '',
          components: {
            modal: ProjectEditModal,
            default: LastSubView
          },
          children: [
            {
              path: 'history',
              name: 'projects#edit_history',
              component: ProjectEditHistory
            },
            {
              path: 'general',
              name: 'projects#edit_general',
              component: ProjectEditGeneral
            },
            {
              path: 'shared',
              name: 'projects#edit_shared',
              component: ProjectEditShared
            }
          ],
          beforeEnter(to, from, next) {
            const map = {
              'projects#edit': ProjectEditMods,
              'projects#edit_settings': ProjectEditSettings,
              'projects#edit_block': ProjectEditBlock,
              'projects#info': ProjectEditInfo
            }
            LastSubView = map[from.name] || ProjectEditMods
            next()
          }
        },
        {
          path: 'settings',
          name: 'projects#edit_settings',
          component: ProjectEditSettings
        },
        {
          path: 'blocks',
          redirect: { name: 'projects#edit' }
        },
        {
          path: 'info',
          name: 'projects#info',
          component: ProjectEditInfo
        },
        {
          path: 'blocks/:block_id',
          name: 'projects#edit_block',
          component: ProjectEditBlock
        },
        {
          path: 'blocks/:block_id/image_map_tool',
          name: 'projects#image_map_tool',
          components: {
            tool: ImageMapTool
          }
        },
        {
          path: 'blocks/:block_id/text_editor',
          name: 'projects#text_editor',
          components: {
            tool: TextEditor
          }
        }
      ]
    },
    {
      path: '/projects/:project_id/preview',
      name: 'projects#preview',
      component: ProjectPreview
    },
    {
      path: '',
      component: PagesLayout,
      children: [
        {
          path: '/guide',
          name: 'pages#guide',
          component: PagesGuide
        },
        {
          path: 'changelog',
          name: 'pages#changelog',
          component: PagesChangelog
        }
      ]
    },
    {
      path: '/404',
      name: 'not_found',
      component: NotFound
    },
    {
      path: '/500',
      name: 'internal_error',
      component: InternalError
    },
    {
      path: '/*',
      redirect: { name: 'not_found' }
    }
  ]
})

router.beforeEach((to, from, next) => {
  // rememer to return early in condition scopes.

  // this route requires login, check if logged in
  // if not, redirect to login page.
  if (to.matched.some(record => record.meta.loginRequired)) {
    if (!store.getters['account/isLoggedIn']) {
      return next({ name: 'signin', query: { redirect: to.fullPath } })
    }
  }

  if (to.matched.some(record => record.meta.canEditProjectRequired)) {
    if (!store.state.account.user.canEditProject) {
      const error = new PermissionError('您沒有編輯專案的權限')
      return next(error)
    }
  }

  if (to.matched.some(record => record.meta.canCreateProjectRequired)) {
    if (!store.state.account.user.canCreateProject) {
      const error = new PermissionError('您沒有新增專案的權限')
      return next(error)
    }
  }

  // make sure to always call next()!
  next()
})

router.afterEach((to, from) => {
  store.dispatch('progress/finish')
  const $loading = document.getElementById('loading-wrapper')
  if ($loading) {
    $loading.remove()
  }
})

router.onError(err => {
  trace('on error', err)

  if (!err) {
    return
  }

  if (err instanceof PermissionError) {
    router.push({ name: 'projects#index' }, () => {
      store.dispatch('alert/set', { error: err })
    }, () => {
      store.dispatch('alert/set', { error: err })
    })
    return
  }

  if (err.code === 404) {
    trace('Got an HTTP status 404 on router error handler, so redirect to `not_found` page.')
    router.push({ name: 'not_found' })
    return
  }

  if (err.code === 401) {
    trace('Got an HTTP status 401 on router error handler, so redirect to `signin` page.')
    router.push({ name: 'signin' })
  }

  if (err.code >= 500) {
    trace('Got an HTTP status >= 500 on router error handler, so redirect to `internal_error` page.')
    router.push({ name: 'internal_error' })
  } else {
    // set alert if not redirect to the 500 error page,
    // because error page already shown the error message.
    store.dispatch('alert/set', { code: err.code })
  }

  store.dispatch('progress/finish', { failed: true })
})

export default router



// WEBPACK FOOTER //
// ./src/router/index.js